// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — ComboSelect Component
// ─────────────────────────────────────────────────────────────────────────────
// Reusable searchable dropdown with keyboard navigation, async loading,
// and optional inline "Create new" action. Short fixed lists use FormControls.Select.
//
// Built on: cmdk (Command primitive) + @radix-ui/react-popover
// No new dependencies — both already in project.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect, useCallback } from 'react';
import { Command } from 'cmdk';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Loader2, X, PlusCircle } from 'lucide-react';
import { fieldHelpAttributes, type FieldHelpProps } from '@/lib/fieldGuidance';

export interface ComboSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  disabled?: boolean;
}

export interface ComboSelectProps extends FieldHelpProps {
  /** Currently selected value */
  value?: string;
  /** Called when a selection is made */
  onChange: (value: string | undefined) => void;
  /** Options to display in the dropdown */
  options: ComboSelectOption[];
  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
  /** Whether options are being loaded */
  isLoading?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether to show a clear (×) button */
  clearable?: boolean;
  /** Called when the search term changes (for async search) */
  onSearch?: (term: string) => void;
  /** If provided, shows "Create '[term]' …" when no results match */
  onCreateOption?: (term: string) => void;
  /** Label for the create option. Default: "Create '{term}'" */
  createLabel?: (term: string) => string;
  /** id for the trigger button (for label association) */
  id?: string;
  /** aria-label when no visible label is available */
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Error state — adds red border */
  hasError?: boolean;
  /** Marks the field as required for assistive technology */
  required?: boolean;
  className?: string;
}

const inputCls =
  'flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 transition-colors';

export default function ComboSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  isLoading = false,
  disabled = false,
  clearable = true,
  onSearch,
  onCreateOption,
  createLabel = (term) => `Create "${term}"`,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  hasError = false,
  required = false,
  className = '',
  help,
  helpLabel,
}: ComboSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const keyboardOpen = useRef(false);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);

  // Measure trigger width for dropdown sizing
  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue === value ? undefined : selectedValue);
      setOpen(false);
      setSearch('');
    },
    [onChange, value],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange(undefined);
      setSearch('');
    },
    [onChange],
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearch(term);
      onSearch?.(term);
    },
    [onSearch],
  );

  const handleCreate = useCallback(() => {
    if (onCreateOption && search.trim().length > 0) {
      onCreateOption(search.trim());
      setOpen(false);
      setSearch('');
    }
  }, [onCreateOption, search]);

  // Filter options client-side when no async handler
  const filteredOptions = onSearch
    ? options
    : options.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          (o.sublabel?.toLowerCase().includes(search.toLowerCase()) ?? false),
      );

  const showCreateOption =
    onCreateOption && search.trim().length >= 2 && filteredOptions.length === 0;

  const borderCls = hasError
    ? 'border-destructive focus-visible:ring-destructive'
    : 'border-input focus-visible:ring-ring';

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        if (!disabled) setOpen(o);
      }}
    >
      <div className="relative min-w-0 w-full">
        <Popover.Trigger asChild>
          <button
            {...fieldHelpAttributes({ id, label: helpLabel ?? ariaLabel ?? placeholder, type: 'select' }, help)}
            ref={triggerRef}
            id={id}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={ariaLabel ?? helpLabel}
            aria-describedby={ariaDescribedBy}
            aria-required={required}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            onPointerDown={() => {
              keyboardOpen.current = false;
            }}
            onKeyDown={(event) => {
              if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
                keyboardOpen.current = true;
                if (event.key.startsWith('Arrow')) {
                  event.preventDefault();
                  setOpen(true);
                }
              }
            }}
            className={`kiq-field ${inputCls} ${borderCls} ${clearable && selectedOption ? 'pr-16' : ''} ${className}`}
          >
            <span
              className={`flex-1 truncate text-left ${!selectedOption ? 'text-muted-foreground' : ''}`}
            >
              {selectedOption ? (
                <span className="flex flex-col">
                  <span>{selectedOption.label}</span>
                  {selectedOption.sublabel && (
                    <span className="text-xs text-muted-foreground">{selectedOption.sublabel}</span>
                  )}
                </span>
              ) : (
                placeholder
              )}
            </span>
            <span className="ml-2 flex items-center gap-1 shrink-0">
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-70" />
            </span>
          </button>
        </Popover.Trigger>
        {clearable && selectedOption && !disabled && (
          <button
            type="button"
            aria-label={`Clear selection: ${selectedOption.label}`}
            onClick={handleClear}
            className="absolute right-9 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="kiq-combo-content animate-in fade-in-0 zoom-in-95"
          style={{
            width: triggerWidth ? `${triggerWidth}px` : 'var(--radix-popover-trigger-width)',
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (keyboardOpen.current) searchRef.current?.focus({ preventScroll: true });
          }}
        >
          <Command shouldFilter={false}>
            {/* Search input */}
            <div className="flex items-center border-b border-border px-3">
              <Command.Input
                ref={searchRef}
                value={search}
                onValueChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
                aria-label={searchPlaceholder}
              />
              {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>

            <Command.List role="listbox" className="max-h-56 overflow-y-auto overflow-x-hidden p-1">
              {/* Empty state */}
              {!isLoading && filteredOptions.length === 0 && !showCreateOption && (
                <Command.Empty className="py-4 text-center text-sm text-muted-foreground">
                  {search.length > 0 ? 'No results found.' : 'No options available.'}
                </Command.Empty>
              )}

              {/* Options list */}
              {filteredOptions.map((option) => (
                <Command.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  onSelect={() => handleSelect(option.value)}
                  onPointerDown={(e) => {
                    if (!option.disabled) {
                      e.preventDefault();
                    }
                  }}
                  className={`kiq-combo-option relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground ${
                    option.disabled
                      ? 'pointer-events-none opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 shrink-0 ${value === option.value ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="flex flex-col min-w-0">
                    <span className="truncate">{option.label}</span>
                    {option.sublabel && (
                      <span className="truncate text-xs text-muted-foreground">
                        {option.sublabel}
                      </span>
                    )}
                  </span>
                </Command.Item>
              ))}

              {/* Create new option */}
              {showCreateOption && (
                <Command.Item
                  value={`__create__${search}`}
                  onSelect={handleCreate}
                  onPointerDown={(e) => {
                    e.preventDefault();
                  }}
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground text-primary"
                >
                  <PlusCircle className="mr-2 h-4 w-4 shrink-0" />
                  <span>{createLabel(search)}</span>
                </Command.Item>
              )}

              {/* Loading placeholder */}
              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
                </div>
              )}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
